/* Filter.js */

"use strict";

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const AdvancedFilter = require("@gluenetworks/entity_grid_gl").AdvancedFilter;

const NETBOX_TO_FILTER_TYPE_MAP = {
    string: "string",
    text: "string",
    longtext: "string",
    boolean: "boolean",
    integer: "number",
    number: "number",
    decimal: "number",
    select: "enum",
    url: "string",
    date: "string",
    datetime: "string",
    json: "string",
};

const ALLOWED_FILTER_TYPES = ["boolean", "string", "enum", "number"];

class Filter extends EventEmitter {
    constructor(
        dataSyncConfigEntity,
        customFieldsWithInherited,
        deviceTypeBaseMap,
        osMap,
        vendorMap,
        sitePathMap,
        skuList,
        metaData,
        gluwareExclusionFilterOptions
    ) {
        super();

        this._nbDataSyncConfigEntity = dataSyncConfigEntity;
        this._tabMainContent = undefined;
        this._gluwareDeviceExclusionFilter = undefined;
        this._netBoxDeviceExclusionFilter = undefined;
        this._customFieldsWithInherited = customFieldsWithInherited;
        this._osMap = osMap;
        this._vendorMap = vendorMap;
        this._sitePathMap = sitePathMap;
        this._skuList = skuList;
        this._deviceTypeBaseMap = deviceTypeBaseMap;
        this._metaData = metaData;
        this._gluwareExclusionFilterOptions = gluwareExclusionFilterOptions;

        this._exclusionFilterMongoQueries = {
            gluwareExclusionFilter: {},
            netBoxExclusionFilter: {},
        };

        // Sources whose saved query could not be loaded into the query builder.
        this._corruptExclusionFilters = new Set();

        this._netBoxStaticFilterOptions = [
            {
                id: "device_type.manufacturer.id",
                name: "device_type.manufacturer.id",
                field: "device_type.manufacturer.id",
                label: "Manufacturer",
                dataType: "enum",
                options: _.get(this._metaData, "netbox.manufacturers", []).map(({id, display}) => ({
                    value: id,
                    label: display,
                })),
            },
            {
                id: "device_type.id",
                name: "device_type.id",
                field: "device_type.id",
                label: "Device Type",
                dataType: "enum",
                options: _.get(this._metaData, "netbox.deviceTypes", []).map(({id, display}) => ({
                    value: id,
                    label: display,
                })),
            },
            {
                id: "platform.id",
                name: "platform.id",
                field: "platform.id",
                label: "Platform",
                dataType: "enum",
                options: _.get(this._metaData, "netbox.platforms", []).map(({id, display}) => ({
                    value: id,
                    label: display,
                })),
            },
            {
                id: "site.id",
                name: "site.id",
                field: "site.id",
                label: "Site",
                dataType: "enum",
                options: _.get(this._metaData, "netbox.sites", []).map(({id, display}) => ({
                    value: id,
                    label: display,
                })),
            },
            {
                id: "role.id",
                name: "role.id",
                field: "role.id",
                label: "Device Role",
                dataType: "enum",
                options: _.get(this._metaData, "netbox.deviceRoles", []).map(({id, display}) => ({
                    value: id,
                    label: display,
                })),
            },
        ];

        this._noConnectionEl = this._createNoConnectionPlaceholder();

        this._onExclusionFilterChanged = this._onExclusionFilterChanged.bind(this);
    }

    /*******************************************/
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _setExclusionFilters(exclusionFilters) {
        const gluwareExclusionFilter = exclusionFilters.gluwareExclusionFilter || {};
        const netBoxExclusionFilter = exclusionFilters.netBoxExclusionFilter || {};

        this._exclusionFilterMongoQueries = {
            gluwareExclusionFilter,
            netBoxExclusionFilter,
        };

        this._applyExclusionFilter(
            "gluware",
            this._gluwareDeviceExclusionFilter,
            gluwareExclusionFilter
        );
        this._applyExclusionFilter(
            "netBox",
            this._netBoxDeviceExclusionFilter,
            netBoxExclusionFilter
        );
    }

    /**
     * Loads one saved exclusion query into its query builder.
     *
     * A saved rule can reference a field that is no longer offered as a filter option, in which
     * case jQuery-QueryBuilder throws and takes the whole NetBox render down with it. Treat that
     * as a corrupt configuration: drop this source only, and leave the other one intact.
     */
    _applyExclusionFilter(source, exclusionFilter, mongoQuery) {
        if (!exclusionFilter) {
            return;
        }

        const builder = exclusionFilter._renderer.$builderContainer;

        // Cancel restores pending patches from the pristine (still corrupt) properties, so the
        // entity has to be re-cleared every time, not just on the first failure.
        if (this._corruptExclusionFilters.has(source)) {
            builder.queryBuilder("reset");
            this._clearExclusionFilterOnEntity(source);
            return;
        }

        if (!mongoQuery || (!mongoQuery.$and && !mongoQuery.$or)) {
            builder.queryBuilder("reset");
            return;
        }

        // Check the fields up front: queryBuilder logs its own console.error for an unknown
        // filter even on the non-throwing path, so feeding it a bad query is noisy either way.
        const unknownFields = this._findUnknownFields(builder, mongoQuery);
        if (unknownFields.length) {
            this._dropCorruptExclusionFilter(
                source,
                builder,
                `references unknown field(s): ${unknownFields.join(", ")}`
            );
            return;
        }

        try {
            builder.queryBuilder("setRulesFromMongo", mongoQuery);
        } catch (error) {
            this._dropCorruptExclusionFilter(source, builder, error);
        }
    }

    /**
     * Field ids in the saved query that the builder has no filter for. Compared against the
     * builder's own registered filters, so it cannot disagree with what setRules would accept.
     */
    _findUnknownFields(builder, mongoQuery) {
        const known = _.map(_.get(builder.data("queryBuilder"), "filters", []), "id");
        if (!known.length) {
            return [];
        }

        return [...this._collectMongoFields(mongoQuery)].filter((field) => !known.includes(field));
    }

    _collectMongoFields(node, fields = new Set()) {
        if (!_.isPlainObject(node)) {
            return fields;
        }

        Object.entries(node).forEach(([key, value]) => {
            if (key === "$and" || key === "$or") {
                (Array.isArray(value) ? value : []).forEach((sub) =>
                    this._collectMongoFields(sub, fields)
                );
            } else if (!key.startsWith("$")) {
                fields.add(key);
            }
        });

        return fields;
    }

    _dropCorruptExclusionFilter(source, builder, reason) {
        console.error(`Unable to load the ${source} exclusion filter:`, reason);
        this._corruptExclusionFilters.add(source);
        builder.queryBuilder("reset");
        this._clearExclusionFilterOnEntity(source);
    }

    /**
     * Drops a corrupt exclusion query from the entity so that saving persists the cleanup and the
     * user can reconfigure this filter from scratch.
     */
    _clearExclusionFilterOnEntity(source) {
        const key = source + "ExclusionFilter";

        _.set(this._exclusionFilterMongoQueries, key, {});
        this._nbDataSyncConfigEntity.setNamespaceMatchingCriteria(
            Object.assign({}, this._nbDataSyncConfigEntity.getNamespaceMatchingCriteria(), {
                [key]: {},
            })
        );
    }

    _resetExclusionFilters() {
        const namespaceMatchingCriteria = _.cloneDeep(
            _.get(this._nbDataSyncConfigEntity, "pristineProperties.namespaceMatchingCriteria", {})
        );

        // Set default empty objects using object spread
        namespaceMatchingCriteria.gluwareExclusionFilter =
            namespaceMatchingCriteria.gluwareExclusionFilter || {};
        namespaceMatchingCriteria.netBoxExclusionFilter =
            namespaceMatchingCriteria.netBoxExclusionFilter || {};

        this._setExclusionFilters(namespaceMatchingCriteria);
    }

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h3");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    _createAdvancedFilter(filterOptions) {
        return new AdvancedFilter({
            filterData: {},
            columnsConfig: this._getFilterOptions(filterOptions),
            allowGroups: 1,
        });
    }

    _createGluwareDeviceExclusionFilter() {
        this._gluwareDeviceExclusionFilter = this._createAdvancedFilter(
            this._gluwareExclusionFilterOptions
        );
        return this._gluwareDeviceExclusionFilter.getRootElement();
    }

    _createNetBoxDeviceExclusionFilter() {
        const filterOptions = [...this._netBoxStaticFilterOptions];

        const deviceProps = _.get(this._metaData, "netbox.deviceProps", {});
        Object.entries(deviceProps).forEach(([key, prop]) => {
            const propKey =
                Array.isArray(prop.path) && prop.path.length > 0 ? prop.path.join(".") : key;
            if (NETBOX_TO_FILTER_TYPE_MAP[prop.type] !== undefined) {
                filterOptions.push({
                    id: propKey,
                    name: propKey,
                    field: propKey,
                    label: prop.displayName.trim(),
                    dataType: NETBOX_TO_FILTER_TYPE_MAP[prop.type],
                });
            }
        });

        const customFields = _.get(this._metaData, "netbox.deviceCustomFields", []);
        customFields.forEach((customField) => {
            if (
                customField.id &&
                customField.display &&
                NETBOX_TO_FILTER_TYPE_MAP[customField.data_type] !== undefined
            ) {
                const dataType = NETBOX_TO_FILTER_TYPE_MAP[customField.data_type];
                const filterOption = {
                    id: `customFieldValues.${customField.name}`,
                    field: `customFieldValues.${customField.name}`,
                    name: `customFieldValues.${customField.name}`,
                    label: customField.display,
                    dataType: dataType,
                };

                if (
                    (dataType === "string" || dataType === "enum") &&
                    !_.isEmpty(customField.choices)
                ) {
                    filterOption.options = _.isObject(customField.choices)
                        ? Object.entries(customField.choices).map(([k, v]) => ({
                              value: k,
                              label: v,
                          }))
                        : [];
                }

                filterOptions.push(filterOption);
            }
        });

        this._netBoxDeviceExclusionFilter = this._createAdvancedFilter(filterOptions);
        return this._netBoxDeviceExclusionFilter.getRootElement();
    }

    _getFilterOptions(filterOptions) {
        return filterOptions
            .filter((option) => {
                if (!option.label) {
                    return false;
                }
                if (
                    option.dataType &&
                    !ALLOWED_FILTER_TYPES.includes(option.dataType.toLowerCase())
                ) {
                    return false;
                }
                return true;
            })
            .map((option) => {
                const dataType = option.dataType ? option.dataType.toLowerCase() : undefined;
                const normalizedOption = Object.assign({}, option, {dataType});
                if (Array.isArray(normalizedOption.options)) {
                    normalizedOption.options = [...normalizedOption.options]
                        .filter((choice) => choice.label !== null && choice.label !== undefined)
                        .sort((a, b) =>
                            String(a.label)
                                .trim()
                                .localeCompare(String(b.label).trim(), undefined, {numeric: true})
                        );
                }
                return normalizedOption;
            })
            .sort((fieldA, fieldB) =>
                fieldA.label.trim().toLowerCase().localeCompare(fieldB.label.trim().toLowerCase())
            );
    }

    _registerListeners() {
        this._gluwareDeviceExclusionFilter.on("AdvancedFilterRuleChanged", (event) => {
            this._onExclusionFilterChanged(event, "gluware");
        });
        this._netBoxDeviceExclusionFilter.on("AdvancedFilterRuleChanged", (event) => {
            this._onExclusionFilterChanged(event, "netBox");
        });
    }

    _unRegisterListeners() {
        this._gluwareDeviceExclusionFilter.removeListener(
            "AdvancedFilterRuleChanged",
            this._onValueChanged
        );
        this._netBoxDeviceExclusionFilter.removeListener(
            "AdvancedFilterRuleChanged",
            this._onValueChanged
        );
    }

    _createNoConnectionPlaceholder() {
        return this._createBanner(
            "Unable to connect to NetBox. Please verify configured connection details.",
            "no-cmdb-connection"
        );
    }

    _createBanner(msg, className) {
        const msgContainer = document.createElement("div");
        msgContainer.classList.add(className);
        const innerSpan = document.createElement("span");
        innerSpan.innerText = msg;

        msgContainer.appendChild(innerSpan);

        return msgContainer;
    }

    _connectionFailed() {
        this._noConnectionEl.classList.add("show");
        this._tabMainContent.classList.add("no-connection");
    }

    _connectionPassed() {
        this._noConnectionEl.classList.remove("show");
        this._tabMainContent.classList.remove("no-connection");
        this._resetExclusionFilters();
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onExclusionFilterChanged(event, source) {
        const sources = ["gluware", "netBox"];

        if (!sources.includes(source)) {
            return;
        }

        let exclusionFilterRules;

        try {
            exclusionFilterRules = event.builder.getRules({skip_empty: true, allow_invalid: true});
        } catch (error) {
            // getRules throws when the builder has no group yet, e.g. straight after a reset.
            // Engines word that differently (V8 "Cannot read properties of null (reading
            // 'each')", Firefox "can't access property \"each\", group is null"), so match the
            // shape instead of exact strings and let anything else through to the console.
            const message = _.get(error, "message", "");
            const isEmptyBuilderError =
                /null|undefined/i.test(message) && /\b(each|root)\b/.test(message);

            if (!isEmptyBuilderError) {
                console.error(error);
            }
        }

        if (_.get(exclusionFilterRules, "rules", []).length > 0) {
            if (exclusionFilterRules.valid) {
                _.set(
                    this._exclusionFilterMongoQueries,
                    source + "ExclusionFilter",
                    event.target.queryBuilder.getMongo(exclusionFilterRules)
                );

                this._nbDataSyncConfigEntity.setNamespaceMatchingCriteria(
                    Object.assign({}, this._nbDataSyncConfigEntity.getNamespaceMatchingCriteria(), {
                        [source + "ExclusionFilter"]:
                            event.target.queryBuilder.getMongo(exclusionFilterRules),
                    })
                );
            }
        } else {
            _.set(this._exclusionFilterMongoQueries, source + "ExclusionFilter", {});

            const otherSource = sources.filter((s) => s !== source)[0];

            const pristineNamespaceMatchingCriteria = _.get(
                this._nbDataSyncConfigEntity.getPristineProperties(),
                "namespaceMatchingCriteria"
            );
            const pristineSourceExclusionFilter = _.get(
                pristineNamespaceMatchingCriteria,
                source + "ExclusionFilter"
            );
            let namespaceMatchingCriteria =
                this._nbDataSyncConfigEntity.getNamespaceMatchingCriteria();

            if (_.isEmpty(pristineSourceExclusionFilter)) {
                namespaceMatchingCriteria = Object.assign({}, namespaceMatchingCriteria, {
                    [source + "ExclusionFilter"]: pristineSourceExclusionFilter,
                });
            } else {
                namespaceMatchingCriteria = Object.assign({}, namespaceMatchingCriteria, {
                    [source + "ExclusionFilter"]: {},
                });
            }
            this._nbDataSyncConfigEntity.setNamespaceMatchingCriteria(namespaceMatchingCriteria);

            if (
                _.isEmpty(_.get(namespaceMatchingCriteria, source + "ExclusionFilter")) &&
                _.isEmpty(_.get(namespaceMatchingCriteria, otherSource + "ExclusionFilter")) &&
                _.isEmpty(pristineNamespaceMatchingCriteria)
            ) {
                this._nbDataSyncConfigEntity.setNamespaceMatchingCriteria(
                    pristineNamespaceMatchingCriteria
                );
            }
        }

        this.emit("ConfigChanged", this._exclusionFilterMongoQueries);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PUBLIC METHODS                    */
    /*******************************************/

    createFilterTab() {
        this._tabMainContent = document.createElement("div");
        this._tabMainContent.classList.add("content-main");

        const gluwareDeviceExclusionFilter = this._createGluwareDeviceExclusionFilter();
        const gluwareDeviceExclusionFilterSection = this._createSection(
            gluwareDeviceExclusionFilter,
            {
                title: "Gluware Device Exclusion Filter",
                bordered: true,
            }
        );

        const netBoxDeviceExclusionFilter = this._createNetBoxDeviceExclusionFilter();
        const netBoxDeviceExclusionFilterSection = this._createSection(
            netBoxDeviceExclusionFilter,
            {
                title: "NetBox Device Exclusion Filter",
                bordered: true,
            }
        );

        this._registerListeners();

        this._tabMainContent.appendChild(this._noConnectionEl);
        this._tabMainContent.appendChild(gluwareDeviceExclusionFilterSection);
        this._tabMainContent.appendChild(netBoxDeviceExclusionFilterSection);

        this._setExclusionFilters(
            _.get(this._nbDataSyncConfigEntity, "pristineProperties.namespaceMatchingCriteria", {
                gluwareExclusionFilter: {},
                netBoxExclusionFilter: {},
            })
        );
    }

    getFilterTabContent() {
        return this._tabMainContent;
    }

    hasCorruptExclusionFilters() {
        return this._corruptExclusionFilters.size > 0;
    }

    validateFilterTab() {
        let isValidGluwareExclusionFilter = false;
        let isValidNetBoxExclusionFilter = false;
        let isEmptyGluwareExclusionFilter = false;
        let isEmptyNetBoxExclusionFilter = false;

        try {
            isEmptyGluwareExclusionFilter =
                this._gluwareDeviceExclusionFilter._renderer._isQueryEmpty();
            isEmptyNetBoxExclusionFilter =
                this._netBoxDeviceExclusionFilter._renderer._isQueryEmpty();
            isValidGluwareExclusionFilter =
                this._gluwareDeviceExclusionFilter._renderer.$builderContainer.queryBuilder(
                    "validate"
                );
            isValidNetBoxExclusionFilter =
                this._netBoxDeviceExclusionFilter._renderer.$builderContainer.queryBuilder(
                    "validate"
                );

            return (
                (isEmptyGluwareExclusionFilter || isValidGluwareExclusionFilter) &&
                (isEmptyNetBoxExclusionFilter || isValidNetBoxExclusionFilter)
            );
        } catch (error) {
            // console.error(error);
        }
    }

    shutdown() {
        this._unRegisterListeners();

        this._customFieldsWithInherited = undefined;
        this._osMap = undefined;
        this._vendorMap = undefined;
        this._sitePathMap = undefined;
        this._skuList = undefined;
        this._deviceTypeBaseMap = undefined;
        this._metaData = undefined;
        this._exclusionFilterMongoQueries = undefined;
        this._noConnectionEl = undefined;
        this._corruptExclusionFilters.clear();

        const components = [
            this._tabMainContent,
            this._gluwareDeviceExclusionFilter,
            this._netBoxDeviceExclusionFilter,
        ];

        return Promise.all(components.map((component) => component.shutdown())).then(() => {
            components.forEach((component, index) => {
                components[index] = undefined;
            });
        });
    }

    setDataSyncEntity(ety) {
        this._nbDataSyncConfigEntity = ety;
        this._corruptExclusionFilters.clear();
    }

    /*******************************************/
    /* END PUBLIC METHODS                      */
    /*******************************************/
}

module.exports = Filter;
